import { Command } from 'cmdk'
import { cn } from 'lib/utils'
import { Search } from 'lucide-react'
import * as React from 'react'
import { Dialog, DialogContent, DialogPortal, DialogTitle } from './Dialog'

interface CommandPaletteProps {
  /** Controlled open state. Recommended — pair with a global cmd-k keybind. */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Placeholder for the input. Defaults to "Search or ask Taka anything..." */
  placeholder?: string
  /** Children should be `CommandGroup` and `CommandItem` from this module. */
  children: React.ReactNode
}

/**
 * Cmd-k command palette. Opens from anywhere in the product and gives the
 * user one input to: navigate ("Calendar"), search ("instagram drafts"),
 * or ask Taka ("turn last week's blog into a post").
 *
 * Built on `cmdk` (Linear / Vercel style) inside a Radix Dialog with the T3
 * dark variant. Keyboard nav, fuzzy search, and screen-reader semantics all
 * come from cmdk; we only own the chrome.
 *
 * Spec: playbook/design-system/components.md#command-palette.
 *
 * Wire a global keybind in App.tsx (or a hook):
 *
 *   const [open, setOpen] = useState(false)
 *   useEffect(() => {
 *     const onKey = (e: KeyboardEvent) => {
 *       if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
 *         e.preventDefault()
 *         setOpen((v) => !v)
 *       }
 *     }
 *     document.addEventListener('keydown', onKey)
 *     return () => document.removeEventListener('keydown', onKey)
 *   }, [])
 *
 *   <CommandPalette open={open} onOpenChange={setOpen}>
 *     <CommandGroup heading="Navigate">
 *       <CommandItem onSelect={() => navigate('/calendar')}>Calendar</CommandItem>
 *     </CommandGroup>
 *     <CommandGroup heading="Ask Taka">
 *       <CommandItem onSelect={...}>Turn this week into a digest</CommandItem>
 *     </CommandGroup>
 *   </CommandPalette>
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onOpenChange,
  placeholder = 'Search or ask Taka anything...',
  children,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogContent
          variant="dark"
          withCloseButton={false}
          size="md"
          // The default DialogContent has padding; the palette wants edge-to-edge
          // input + list, so override.
          className="overflow-hidden !p-0"
        >
          {/* DialogTitle is required by Radix for screen readers. Hidden visually. */}
          <DialogTitle variant="dark" className="sr-only">
            Command palette
          </DialogTitle>
          <Command
            label={placeholder}
            // Suppress cmdk's default scoring on empty query so all groups
            // render until the user types — feels more like a navigator.
            shouldFilter
            className="flex flex-col"
          >
            <div className="flex items-center gap-3 border-b border-line-bright px-4 py-3">
              <Search size={16} className="shrink-0 text-paper-faint" />
              <Command.Input
                placeholder={placeholder}
                className="flex-1 bg-transparent text-[15px] text-paper outline-none placeholder:text-paper-faint"
              />
              <kbd className="mono inline-flex h-5 items-center rounded-[4px] border border-line-bright px-1.5 text-[10px] text-paper-faint">
                ESC
              </kbd>
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-2">
              <Command.Empty className="py-8 text-center text-[14px] text-paper-dim">
                Nothing matches that. Try fewer words.
              </Command.Empty>
              {children}
            </Command.List>
          </Command>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

interface CommandGroupProps {
  heading: string
  children: React.ReactNode
  className?: string
}

/** A labeled section in the palette. Use a few groups to organize commands ("Navigate", "Ask Taka", "Recent"). */
export const CommandGroup: React.FC<CommandGroupProps> = ({ heading, children, className }) => (
  <Command.Group
    heading={heading}
    className={cn(
      // cmdk renders the heading in a div with `[cmdk-group-heading]` data attr;
      // style it as a mono eyebrow.
      '[&_[cmdk-group-heading]]:mono [&_[cmdk-group-heading]]:text-paper-faint',
      '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-2',
      '[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:tracking-[0.2em] [&_[cmdk-group-heading]]:uppercase',
      className
    )}
  >
    {children}
  </Command.Group>
)

interface CommandItemProps {
  /** Stable key. Defaults to children when string. */
  value?: string
  /** Optional left icon (16px lucide). */
  icon?: React.ReactNode
  /** Optional right slot — typically a kbd shortcut hint. */
  shortcut?: React.ReactNode
  onSelect: () => void
  children: React.ReactNode
  disabled?: boolean
  className?: string
}

/** A row in the palette. Supports left icon, shortcut hint, and onSelect handler. */
export const CommandItem: React.FC<CommandItemProps> = ({
  value,
  icon,
  shortcut,
  onSelect,
  children,
  disabled,
  className,
}) => (
  <Command.Item
    value={value}
    onSelect={onSelect}
    disabled={disabled}
    className={cn(
      'flex cursor-pointer items-center gap-3 rounded-[8px] px-2 py-2 text-[14px]',
      // cmdk sets `data-selected` on the item the user is on.
      'text-paper-dim',
      'data-[selected=true]:bg-bg-2 data-[selected=true]:text-paper',
      'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-40',
      className
    )}
  >
    {icon && (
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-paper-faint">
        {icon}
      </span>
    )}
    <span className="flex-1 truncate">{children}</span>
    {shortcut && (
      <span className="mono ml-auto text-[10px] tracking-[0.05em] text-paper-faint">
        {shortcut}
      </span>
    )}
  </Command.Item>
)
